// src/hooks/useGrowthSeries.jsx
// Carrega o ChartResponse do `/grafico` para N experimentos numa MÉTRICA única,
// sob demanda e com cache. Diferente do useCharts (que é por-experimentId), este
// gerencia a lista inteira: busca só a métrica ativa, em paralelo com teto de
// concorrência, e cacheia por (experimentId, métrica) — então trocar de aba não
// refaz o que já veio.
import { useEffect, useMemo, useRef, useState } from 'react';
import { getGrafico } from '../infra/api/GraficoApi';

const CONCURRENCY = 4;

export function useGrowthSeries(experimentIds, metric, enabled = true) {
    const cacheRef = useRef(new Map()); // `${id}::${metric}` -> { data } | { error }
    const inFlightRef = useRef(new Set());
    const [version, setVersion] = useState(0); // bump força recomputo ao mudar o cache

    const ids = useMemo(
        () =>
            Array.isArray(experimentIds)
                ? [...new Set(experimentIds.filter(Boolean).map(String))]
                : [],
        [experimentIds]
    );
    const idsKey = ids.join(',');

    useEffect(() => {
        if (!enabled || !metric || !idsKey) return undefined;
        let cancelled = false;

        const idList = idsKey.split(',');
        const keyOf = (id) => `${id}::${metric}`;
        const queue = idList.filter((id) => {
            const k = keyOf(id);
            return !cacheRef.current.has(k) && !inFlightRef.current.has(k);
        });
        if (queue.length === 0) return undefined;

        const runWorker = async () => {
            while (queue.length && !cancelled) {
                const id = queue.shift();
                const k = keyOf(id);
                inFlightRef.current.add(k);
                try {
                    const data = await getGrafico(id, metric);
                    cacheRef.current.set(k, { data });
                } catch (e) {
                    cacheRef.current.set(k, {
                        error: e?.message || 'Erro ao carregar gráfico',
                    });
                } finally {
                    inFlightRef.current.delete(k);
                    if (!cancelled) setVersion((v) => v + 1);
                }
            }
        };

        const n = Math.min(CONCURRENCY, queue.length);
        for (let i = 0; i < n; i++) runWorker();

        return () => {
            cancelled = true;
        };
    }, [enabled, metric, idsKey]);

    return useMemo(() => {
        const seriesByExp = {};
        const errors = {};
        let done = 0;
        ids.forEach((id) => {
            const entry = cacheRef.current.get(`${id}::${metric}`);
            if (!entry) return;
            done += 1;
            if (entry.data) seriesByExp[id] = entry.data;
            if (entry.error) errors[id] = entry.error;
        });
        const total = ids.length;
        return {
            seriesByExp,
            errors,
            loading: enabled && total > 0 && done < total,
            progress: { done, total },
        };
        // `version` entra nas deps de propósito: o cache é um ref (não reativo),
        // então o bump é o que dispara o recomputo quando um fetch termina.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idsKey, metric, enabled, version]);
}
